import { useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../../utils/axiosInstance";
import { AuthContext } from "../../../context/AuthContext";
import Panel from "../../../components/Dashboard/Panel";
import StatTile from "../../../components/Dashboard/StatTile";
import DataTable from "../../../components/Dashboard/DataTable";
import Badge from "../../../components/Dashboard/Badge";
import PageHeader from "../../../components/Dashboard/PageHeader";
import { displayNameOf, fmtDate, greetingFor } from "../../../components/Dashboard/dashboardKit";
import { Users, UserPlus, DollarSign, Inbox } from "lucide-react";

const TYPE_BADGE = {
  Company:    "bg-brand-50 text-brand-700",
  Individual: "bg-violet-100 text-violet-700",
};

const VendorManagerDashboard = () => {
  const { user } = useContext(AuthContext);
  const [counts, setCounts] = useState({ vendors: null, applications: null, contacts: null, prices: null });
  const [recentVendors, setRecentVendors] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const countOf = (res) =>
      res?.data?.meta?.total ?? (Array.isArray(res?.data?.data) ? res.data.data.length : 0);

    const fetchAll = async () => {
      const [vendorsRes, contactsRes, pricesRes] = await Promise.allSettled([
        api.get("/vendors"),
        api.get("/vendor/contact-persons"),
        api.get("/vendor-price-list"),
      ]);

      const vendors =
        vendorsRes.status === "fulfilled" ? vendorsRes.value.data?.data ?? [] : [];

      setCounts({
        vendors: vendors.length,
        applications: null,
        contacts: contactsRes.status === "fulfilled" ? countOf(contactsRes.value) : null,
        prices: pricesRes.status === "fulfilled" ? countOf(pricesRes.value) : null,
      });

      setRecentVendors(
        [...vendors]
          .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0))
          .slice(0, 5)
      );
      setLoading(false);
    };

    fetchAll();
  }, []);

  const vendorName = (v) =>
    v.type === "Company"
      ? v.company_name || "—"
      : `${v.primary_users?.first_name || ""} ${v.primary_users?.last_name || ""}`.trim() || "—";

  return (
    <div className="space-y-5">
      <PageHeader
        title="Dashboard"
        subtitle={`${greetingFor(displayNameOf(user, "Manager"))}. Vendor activity at a glance.`}
      />

      <div className="grid grid-cols-2 gap-4 xl:grid-cols-4">
        <StatTile icon={Users}      label="Vendors"      value={counts.vendors}      tone="brand"   to="/vendors"             loading={loading} />
        <StatTile icon={Inbox}      label="Applications" value={counts.applications} tone="emerald" to="/vendor/applications" loading={loading} />
        <StatTile icon={UserPlus}   label="Contacts"     value={counts.contacts}     tone="violet"  to="/vendor/contacts"     loading={loading} />
        <StatTile icon={DollarSign} label="Price lists"  value={counts.prices}       tone="indigo"  to="/vendor/prices"       loading={loading} />
      </div>

      <div className="grid gap-4">
        <Panel
          title="Recent vendors"
          subtitle="The five most recently added"
          action={
            <Link to="/vendors" className="text-xs font-semibold text-brand-600 hover:text-brand-700 hover:underline">
              View all
            </Link>
          }
        >
          <DataTable
            loading={loading}
            rows={recentVendors}
            empty="No vendors yet."
            columns={[
              {
                key: "name",
                header: "Vendor",
                cell: (v) => (
                  <Link
                    to={`/vendors/${v.id}`}
                    className="block max-w-[13rem] truncate text-sm font-medium text-gray-800 hover:text-brand-600"
                  >
                    {vendorName(v)}
                  </Link>
                ),
              },
              {
                key: "type",
                header: "Type",
                cell: (v) => (
                  <Badge className={TYPE_BADGE[v.type] ?? "bg-gray-100 text-gray-600"}>
                    {v.type || "—"}
                  </Badge>
                ),
              },
              {
                key: "country",
                header: "Country",
                className: "hidden sm:table-cell",
                cell: (v) => <span className="text-sm text-gray-500">{v.country || "—"}</span>,
              },
              {
                key: "added",
                header: "Added",
                cell: (v) => <span className="text-sm text-gray-500">{fmtDate(v.createdAt)}</span>,
              },
            ]}
          />
        </Panel>
      </div>
    </div>
  );
};

export default VendorManagerDashboard;